import dayjs from 'dayjs'
import React from 'react'
import { Task } from 'types'

const toArray = (v?: string | string[]) => (Array.isArray(v) ? v : v ? [v] : [])
const isOwnedBy = (owner: string | string[] | undefined, userId?: string) => !!userId && toArray(owner).includes(userId)
const isQueued = (queue: string | string[] | undefined, userId?: string) => !!userId && toArray(queue).includes(userId)
const isTeam = (teamIds: string[] | undefined, userId?: string) =>
  !!userId && Array.isArray(teamIds) && teamIds.includes(userId)
// NEW
const isAssigner = (assigner: string | string[] | undefined, userId?: string) =>
  !!userId && toArray(assigner).includes(userId)

const getTaskId = (t: any) => t.SK ?? t.id ?? `${t.PK ?? ''}#${t.SK ?? ''}`

const determineStaleTask = (task: Task) => {
  if (!task?.SK?.startsWith('MONTHLY_TASK_')) return false
  const createdDate = dayjs(task?.timeCreated)
  const currentMonth = dayjs().month()
  const currentYear = dayjs().year()
  return !(createdDate.year() === currentYear && createdDate.month() === currentMonth)
}

type Buckets = {
  byOwner: Task[]
  byQueue: Task[]
  byTeam: Task[]
  byAssigner: Task[] // ✅ all tasks where user is assigner (excludes stale monthlies)
  anyMatch: Task[]
  allMatch: Task[]
  completed: {
    byOwner: Task[]
    byQueue: Task[]
    byTeam: Task[]
    byAssigner: Task[] // already had this from earlier step
    any: Task[]
    allMatch: Task[]
    ownerOrAssigner: Task[] // if you kept this from the last revision
    ownerAndAssigner: Task[]
  }
  monthly: {
    all: Task[]
    current: Task[]
    stale: Task[]
    byOwnerCurrent: Task[]
    byQueueCurrent: Task[]
    byTeamCurrent: Task[]
    byAssignerCurrent: Task[] // ✅ current-month monthlies assigned by user
    anyCurrent: Task[]
    allCurrent: Task[]
  }
}

export const useMyTasks = (tasks: Task[] | undefined, viewUserId?: string) =>
  React.useMemo<Buckets>(() => {
    const acc: Buckets = {
      byOwner: [],
      byQueue: [],
      byTeam: [],
      byAssigner: [], // ✅
      anyMatch: [],
      allMatch: [],
      completed: {
        byOwner: [],
        byQueue: [],
        byTeam: [],
        byAssigner: [],
        any: [],
        allMatch: [],
        ownerOrAssigner: [],
        ownerAndAssigner: [],
      },
      monthly: {
        all: [],
        current: [],
        stale: [],
        byOwnerCurrent: [],
        byQueueCurrent: [],
        byTeamCurrent: [],
        byAssignerCurrent: [], // ✅
        anyCurrent: [],
        allCurrent: [],
      },
    }

    const seenAny = new Set<string>()
    const seenMonthlyAny = new Set<string>()
    const seenCompletedAny = new Set<string>()
    const seenCompletedOwnerOrAssigner = new Set<string>()

    for (const t of tasks ?? []) {
      const isMonthly = t?.SK?.startsWith('MONTHLY_TASK_') ?? false
      const staleMonthly = isMonthly ? determineStaleTask(t) : false

      const ownerSource = (t as any)?.owner
      const queueSource = (t as any)?.queue?.user_uuid ?? (t as any)?.queue
      const assignerSource = (t as any)?.assigner?.user_uuid ?? (t as any)?.assigner
      const team_details = Array.isArray((t as any)?.team_details)
        ? (t as any).team_details.map((u: any) => u?.user_uuid).filter(Boolean)
        : []
      const assignees = Array.isArray((t as any)?.assignees)
        ? (t as any).assignees.map((i: any) => i?.user_uuid ?? i)
        : []
      const teamIds = Array.from(new Set([...team_details, ...assignees])) ?? []

      const owned = isOwnedBy(ownerSource, viewUserId)
      const queued = isQueued(queueSource, viewUserId)
      const onTeam = isTeam(teamIds, viewUserId)
      const assignedByMe = isAssigner(assignerSource, viewUserId)

      const hitsMain = (owned ? 1 : 0) + (queued ? 1 : 0) + (onTeam ? 1 : 0)
      const id = getTaskId(t)
      const done = (t?.masterStatus ?? '').toString().trim().toLowerCase().startsWith('done')

      // ----- Monthly buckets -----
      if (isMonthly) {
        acc.monthly.all.push(t)
        if (staleMonthly) acc.monthly.stale.push(t)
        else acc.monthly.current.push(t)

        if (!staleMonthly) {
          if (owned) acc.monthly.byOwnerCurrent.push(t)
          if (queued) acc.monthly.byQueueCurrent.push(t)
          if (onTeam) acc.monthly.byTeamCurrent.push(t)
          if (assignedByMe) acc.monthly.byAssignerCurrent.push(t) // ✅

          if (hitsMain > 0 && !seenMonthlyAny.has(id)) {
            acc.monthly.anyCurrent.push(t)
            seenMonthlyAny.add(id)
          }
          if (hitsMain === 3) acc.monthly.allCurrent.push(t)
        }
      }

      // Exclude stale monthlies from main/completed buckets
      if (staleMonthly) continue

      // ----- Main buckets -----
      if (owned) acc.byOwner.push(t)
      if (queued) acc.byQueue.push(t)
      if (onTeam) acc.byTeam.push(t)
      if (assignedByMe) acc.byAssigner.push(t) // ✅

      if (hitsMain > 0 && !seenAny.has(id)) {
        acc.anyMatch.push(t)
        seenAny.add(id)
      }
      if (hitsMain === 3) acc.allMatch.push(t)

      // ----- Completed buckets -----
      if (done) {
        if (owned) acc.completed.byOwner.push(t)
        if (queued) acc.completed.byQueue.push(t)
        if (onTeam) acc.completed.byTeam.push(t)
        if (assignedByMe) acc.completed.byAssigner.push(t)

        if ((owned || queued || onTeam || assignedByMe) && !seenCompletedAny.has(id)) {
          acc.completed.any.push(t)
          seenCompletedAny.add(id)
        }
        if ((owned || assignedByMe) && !seenCompletedOwnerOrAssigner.has(id)) {
          acc.completed.ownerOrAssigner.push(t)
          seenCompletedOwnerOrAssigner.add(id)
        }
        if (owned && assignedByMe) acc.completed.ownerAndAssigner.push(t)
      }
    }

    return acc
  }, [tasks, viewUserId])
